import { useAdminHealth } from '../useAdmin';
import './AdminHealth.css';

/**
 * Admin system health (F17). Live snapshot of subsystem checks
 * (db, storage, moderation, email). Refetches every 30s so an open
 * dashboard reflects fresh state without manual reload.
 */
export function AdminHealth(): JSX.Element {
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminHealth();

  if (isLoading) {
    return <p className="bl-admin-health__notice">Loading health…</p>;
  }
  if (isError) {
    return (
      <p className="bl-admin-health__notice bl-admin-health__notice--error" role="alert">
        Could not load health ({error instanceof Error ? error.message : 'unknown error'}).
      </p>
    );
  }
  if (!data) return <p>No data.</p>;

  return (
    <section className="bl-admin-health" aria-label="System health">
      <header className="bl-admin-health__header">
        <div>
          <h2>System health</h2>
          <p
            className={
              data.overall_ok
                ? 'bl-admin-health__overall bl-admin-health__overall--ok'
                : 'bl-admin-health__overall bl-admin-health__overall--bad'
            }
          >
            {data.overall_ok ? 'All checks passing.' : 'One or more checks need attention.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="bl-admin-health__refresh"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <ul className="bl-admin-health__list">
        {data.checks.map((c) => (
          <li key={c.name} className="bl-admin-health__entry" data-ok={c.ok}>
            <div className="bl-admin-health__entry-head">
              <span
                className={
                  c.ok
                    ? 'bl-admin-health__pill bl-admin-health__pill--ok'
                    : 'bl-admin-health__pill bl-admin-health__pill--bad'
                }
              >
                {c.ok ? 'ok' : 'fail'}
              </span>
              <span className="bl-admin-health__name">{c.name}</span>
            </div>
            {c.detail && <p className="bl-admin-health__detail">{c.detail}</p>}
          </li>
        ))}
      </ul>

      <section className="bl-admin-health__providers">
        <h3>Providers</h3>
        <dl>
          <dt>Database round-trip</dt>
          <dd>{data.db_latency_ms}ms</dd>
          <dt>Storage</dt>
          <dd>{data.storage_provider}</dd>
          <dt>CSAM scanner</dt>
          <dd>{data.csam_provider}</dd>
          <dt>Moderation</dt>
          <dd>{data.moderation_provider}</dd>
          <dt>Email</dt>
          <dd>{data.email_provider}</dd>
        </dl>
      </section>
    </section>
  );
}
